import React, { useRef, useEffect, useState } from 'react';
import Icon from '../AppIcon';
import { buildEmbedUrl } from '../../utils/videoUrlBuilder';

const YouTubePlayer = ({ 
  url, 
  videoId, // Novo: receber videoId diretamente
  config = {}, 
  onTimeUpdate, 
  onComplete, 
  currentTime, 
  duration,
  className = "w-full h-full"
}) => {
  const iframeRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(true);

  // Determinar video ID: priorizar prop videoId, fallback para extração da URL
  const finalVideoId = React.useMemo(() => {
    if (videoId) {
      console.log('✅ YouTubePlayer: Usando videoId da prop:', videoId);
      return videoId;
    }
    
    if (url) {
      console.log('🔄 YouTubePlayer: Extraindo videoId da URL:', url);
      // Função para extrair ID da URL (fallback para compatibilidade)
      const getYouTubeVideoId = (url) => {
        const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
        const match = url.match(regExp);
        return (match && match[2].length === 11) ? match[2] : null;
      };
      
      const extractedId = getYouTubeVideoId(url);
      console.log('🔍 YouTubePlayer: Video ID extraído da URL:', extractedId);
      return extractedId;
    }
    
    console.log('❌ YouTubePlayer: Nenhum videoId ou URL fornecidos');
    return null;
  }, [videoId, url]);

  // Construir URL de embed usando o VideoURLBuilder
  const embedUrl = React.useMemo(() => {
    if (!finalVideoId) return null;
    
    try {
      const url = buildEmbedUrl('youtube', { video_id: finalVideoId }, {
        autoplay: config.autoplay || false,
        controls: config.controls !== false,
        start: currentTime || 0,
        ...config
      });
      console.log('✅ YouTubePlayer: URL de embed construída:', url);
      return url;
    } catch (error) {
      console.error('❌ YouTubePlayer: Erro ao construir URL:', error);
      return null;
    }
  }, [finalVideoId, config, currentTime]);

  useEffect(() => {
    if (embedUrl) {
      setIsLoading(false);
    }
  }, [embedUrl]);

  const handleMouseMove = () => {
    setShowControls(true);
    clearTimeout(handleMouseMove.timeout);
    handleMouseMove.timeout = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  if (!embedUrl) {
    console.log('❌ YouTubePlayer: embedUrl é null/undefined. URL original:', url);
    console.log('❌ YouTubePlayer: videoId recebido:', videoId);
    console.log('❌ YouTubePlayer: finalVideoId:', finalVideoId);
    return (
      <div className={`${className} bg-black flex items-center justify-center`}>
        <div className="text-white text-center">
          <Icon name="AlertCircle" size={48} className="mx-auto mb-4" />
          <p>URL do YouTube inválida</p>
          <p className="text-sm mt-2">URL: {url || 'não fornecida'}</p>
          <p className="text-sm">Video ID: {finalVideoId || 'não encontrado'}</p>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`${className} relative bg-black`}
      onMouseMove={handleMouseMove}
    >
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <div className="flex flex-col items-center space-y-4 text-white">
            <div className="animate-spin">
              <Icon name="Loader2" size={40} />
            </div>
            <p>Carregando vídeo do YouTube...</p>
          </div>
        </div>
      )}

      <iframe
        ref={iframeRef}
        src={embedUrl}
        className="w-full h-full"
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        onLoad={() => setIsLoading(false)}
      />

      {/* Overlay de controles (simulado) */}
      {!isLoading && showControls && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4">
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30 transition-colors"
              >
                <Icon name={isPlaying ? "Pause" : "Play"} size={20} />
              </button>
              <span className="text-sm">YouTube Player</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Maximize" size={20} className="cursor-pointer hover:text-gray-300" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default YouTubePlayer;
